export type MobileAction = {
  createdAt: string;
  id: string;
  payload: Record<string, unknown>;
  timezone: string;
  type: string;
};

export type MobileEvidenceField = "photoFileId" | "photoFileIds" | "signatureFileId" | `form:${string}`;
export type MobileEvidence = { actionId: string; field: MobileEvidenceField; file: File; id: string; uploadedFileId?: string };
export type MobileEvidenceReference = { field: MobileEvidenceField; id: string };

type StoredAction = Omit<MobileAction, "payload"> & { ciphertext: ArrayBuffer; iv: Uint8Array<ArrayBuffer> };
type StoredEvidence = { actionId: string; ciphertext: ArrayBuffer; contentType: string; field: MobileEvidenceField; fileName: string; id: string; iv: Uint8Array<ArrayBuffer>; size: number; uploadedFileId?: string };

const databaseName = "threadops-mobile-v1";
const keyStore = "keys";
const actionStore = "actions";
const evidenceStore = "evidence";
const payloadLimit = 64 * 1024;

export async function queueMobileAction(type: string, payload: Record<string, unknown>) {
  if (!/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/.test(type)) throw new Error("Invalid mobile action type");
  const encoded = new TextEncoder().encode(JSON.stringify(payload));
  if (encoded.byteLength > payloadLimit) throw new Error("Mobile action exceeds the offline payload limit");
  const database = await openDatabase();
  const key = await encryptionKey(database);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ iv, name: "AES-GCM" }, key, encoded);
  const action: StoredAction = { ciphertext, createdAt: new Date().toISOString(), id: crypto.randomUUID(), iv, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", type };
  await request(transaction(database, actionStore, "readwrite").put(action));
  database.close();
  window.dispatchEvent(new CustomEvent("threadops:mobile-action-queued"));
  return action.id;
}

export async function queueMobileActionWithEvidence(type: string, payload: Record<string, unknown>, inputs: Array<{ field: MobileEvidenceField; file: File }>) {
  if (!inputs.length) return queueMobileAction(type, payload);
  if (!/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/.test(type)) throw new Error("Invalid mobile action type");
  const formTypes = new Set(["application/pdf", "application/zip", "image/jpeg", "image/png", "image/webp", "text/csv", "text/plain"]);
  if (inputs.some(input => input.field.startsWith("form:")
    ? !formTypes.has(input.file.type) || !input.file.size || input.file.size > 25 * 1024 * 1024
    : !["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"].includes(input.file.type) || !input.file.size || input.file.size > 15 * 1024 * 1024)) throw new Error("Offline evidence type or size is not supported");
  const actionId = crypto.randomUUID();
  const references: MobileEvidenceReference[] = inputs.map(input => ({ field: input.field, id: crypto.randomUUID() }));
  const actionPayload = { ...payload, offlineEvidence: references };
  const encoded = new TextEncoder().encode(JSON.stringify(actionPayload));
  if (encoded.byteLength > payloadLimit) throw new Error("Mobile action exceeds the offline payload limit");
  const database = await openDatabase();
  const key = await encryptionKey(database);
  const actionIv = crypto.getRandomValues(new Uint8Array(12));
  const actionCiphertext = await crypto.subtle.encrypt({ iv: actionIv, name: "AES-GCM" }, key, encoded);
  const evidence = await Promise.all(inputs.map(async (input, index): Promise<StoredEvidence> => {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ iv, name: "AES-GCM" }, key, await input.file.arrayBuffer());
    return { actionId, ciphertext, contentType: input.file.type, field: input.field, fileName: input.file.name, id: references[index].id, iv, size: input.file.size };
  }));
  const tx = database.transaction([actionStore, evidenceStore], "readwrite");
  tx.objectStore(actionStore).put({ ciphertext: actionCiphertext, createdAt: new Date().toISOString(), id: actionId, iv: actionIv, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", type } satisfies StoredAction);
  for (const record of evidence) tx.objectStore(evidenceStore).put(record);
  await transactionComplete(tx);
  database.close();
  window.dispatchEvent(new CustomEvent("threadops:mobile-action-queued"));
  return actionId;
}

export async function readMobileActions(limit = 100): Promise<MobileAction[]> {
  const database = await openDatabase();
  const key = await encryptionKey(database);
  const records = (await request(transaction(database, actionStore).getAll(null, Math.min(Math.max(limit, 1), 100)))) as StoredAction[];
  const actions = await Promise.all(records.map(async (record) => {
    const plaintext = await crypto.subtle.decrypt({ iv: record.iv, name: "AES-GCM" }, key, record.ciphertext);
    return { createdAt: record.createdAt, id: record.id, payload: JSON.parse(new TextDecoder().decode(plaintext)) as Record<string, unknown>, timezone: record.timezone, type: record.type };
  }));
  database.close();
  return actions;
}

export async function removeMobileActions(ids: string[]) {
  if (!ids.length) return;
  const database = await openDatabase();
  const store = transaction(database, actionStore, "readwrite");
  await Promise.all(ids.map((id) => request(store.delete(id))));
  database.close();
  await removeMobileEvidenceForActions(ids);
}

export async function readMobileEvidence(id: string): Promise<MobileEvidence | null> {
  const database = await openDatabase();
  const record = await request(transaction(database, evidenceStore).get(id)) as StoredEvidence | undefined;
  if (!record) { database.close(); return null; }
  const key = await encryptionKey(database);
  const plaintext = await crypto.subtle.decrypt({ iv: record.iv, name: "AES-GCM" }, key, record.ciphertext);
  const evidence = { actionId: record.actionId, field: record.field, file: new File([plaintext], record.fileName, { type: record.contentType }), id: record.id, uploadedFileId: record.uploadedFileId };
  database.close();
  return evidence;
}

export async function markMobileEvidenceUploaded(id: string, uploadedFileId: string) {
  const database = await openDatabase();
  const record = await request(transaction(database, evidenceStore).get(id)) as StoredEvidence | undefined;
  if (!record) { database.close(); throw new Error("Offline evidence is unavailable"); }
  await request(transaction(database, evidenceStore, "readwrite").put({ ...record, uploadedFileId }));
  database.close();
}

async function encryptionKey(database: IDBDatabase) {
  const existing = await request(transaction(database, keyStore).get("queue")) as CryptoKey | undefined;
  if (existing) return existing;
  const key = await crypto.subtle.generateKey({ length: 256, name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  await request(transaction(database, keyStore, "readwrite").put(key, "queue"));
  return key;
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(databaseName, 2);
    open.onupgradeneeded = (event) => {
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
      if (oldVersion < 1) { open.result.createObjectStore(keyStore); open.result.createObjectStore(actionStore, { keyPath: "id" }); }
      if (oldVersion < 2) open.result.createObjectStore(evidenceStore, { keyPath: "id" }).createIndex("actionId", "actionId");
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

async function removeMobileEvidenceForActions(actionIds: string[]) {
  const database = await openDatabase();
  const records = await request(transaction(database, evidenceStore).getAll()) as StoredEvidence[];
  const store = transaction(database, evidenceStore, "readwrite");
  await Promise.all(records.filter(record => actionIds.includes(record.actionId)).map(record => request(store.delete(record.id))));
  database.close();
}

function transaction(database: IDBDatabase, store: string, mode: IDBTransactionMode = "readonly") { return database.transaction(store, mode).objectStore(store); }
function request<T>(value: IDBRequest<T>) { return new Promise<T>((resolve, reject) => { value.onsuccess = () => resolve(value.result); value.onerror = () => reject(value.error); }); }
function transactionComplete(value: IDBTransaction) { return new Promise<void>((resolve, reject) => { value.oncomplete = () => resolve(); value.onerror = () => reject(value.error); value.onabort = () => reject(value.error); }); }
